/**
 * @fileoverview My Page filter state helpers.
 * @description The My Page table's filter values as the screen holds them (every dropdown has an
 * "All" choice, the search box holds what was typed), their defaults, whether any is active, and
 * the translation into the list endpoint's filters, which leave out whatever is "All" or blank.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type {
  MyPagePriority,
  MyPageStageCode,
  MyPageTargetFilters,
} from '@/features/sales/types/my-page.types'

/** @description The dropdowns' "no filter" value. */
export const MY_PAGE_ALL = 'All'

/**
 * @class
 * @description The table's filters as the screen holds them.
 */
export interface MyPageFilterState {
  query: string
  stage: MyPageStageCode | typeof MY_PAGE_ALL
  status: NonNullable<MyPageTargetFilters['status']> | typeof MY_PAGE_ALL
  priority: MyPagePriority | typeof MY_PAGE_ALL
  includeClosed: boolean
}

/** @description Every filter off: closed targets are shown unless hidden. */
export const DEFAULT_MY_PAGE_FILTERS: MyPageFilterState = {
  query: '',
  stage: MY_PAGE_ALL,
  status: MY_PAGE_ALL,
  priority: MY_PAGE_ALL,
  includeClosed: true,
}

/**
 * @description Whether any filter differs from its default.
 * @param {MyPageFilterState} state - The screen's filters.
 * @returns {boolean} True when at least one is active.
 */
export function hasActiveMyPageFilters(state: MyPageFilterState): boolean {
  return (
    state.query.trim() !== '' ||
    state.stage !== MY_PAGE_ALL ||
    state.status !== MY_PAGE_ALL ||
    state.priority !== MY_PAGE_ALL ||
    !state.includeClosed
  )
}

/**
 * @description Turns the screen's filters into the list endpoint's, leaving out every "All" and a
 * blank search.
 * @param {MyPageFilterState} state - The screen's filters.
 * @param {string} search - The search text to send (the debounced query).
 * @returns {MyPageTargetFilters} The endpoint's filters.
 */
export function toMyPageTargetFilters(
  state: MyPageFilterState,
  search: string,
): MyPageTargetFilters {
  return {
    ...(search ? { search } : {}),
    ...(state.stage !== MY_PAGE_ALL ? { stage: state.stage } : {}),
    ...(state.status !== MY_PAGE_ALL ? { status: state.status } : {}),
    ...(state.priority !== MY_PAGE_ALL ? { priority: state.priority } : {}),
    includeClosed: state.includeClosed,
  }
}
